/**
 * One typed key's control (ORDR-119, seam C4).
 *
 * EMPTY MEANS ABSENT, and absent means the reader's default, which the control
 * names. "Remove this key" empties it. The capacity coefficient also reads as
 * a percentage, and says before any save that 0 stops new orders.
 *
 * A COLOUR IS TYPED AS HEX (ORDR-175), because the hex is what the server
 * validates and stores; the picker beside it only fills that text in. It shows
 * the colour once the text is one, and black (the browser's own empty value)
 * until then. A logo URL is a `type="url"` box and nothing more: the server
 * owns its rule.
 */

import type { ReactElement } from "react";
import { isBrandHex } from "@pirouz-ca/ui-tokens";
import type { FieldRule } from "./fields.js";

export interface TypedControlProps {
  readonly rule: FieldRule;
  /** What is typed; "" is absent. */
  readonly raw: string;
  /** A client-side rule failure, or the server's sentence for this key. */
  readonly problem: string | null;
  readonly onChange: (raw: string) => void;
}

const COEFFICIENT = "capacity_coefficient_permille";

export function TypedControl({ rule, raw, problem, onChange }: TypedControlProps): ReactElement {
  const inputClass = "mt-1 w-full rounded border border-[var(--color-neutral-300)] p-2";
  return (
    <div className="text-sm">
      <label>
        {rule.label}
        {rule.text !== undefined ? (
          <input
            type={rule.text === "url" ? "url" : "text"}
            value={raw}
            onChange={(event) => onChange(event.target.value)}
            className={inputClass}
          />
        ) : rule.choices === undefined ? (
          <input
            type="number"
            inputMode="numeric"
            value={raw}
            onChange={(event) => onChange(event.target.value)}
            className={inputClass}
          />
        ) : (
          <select value={raw} onChange={(event) => onChange(event.target.value)} className={inputClass}>
            <option value="">Default ({rule.absentMeans})</option>
            {rule.choices.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        )}
      </label>
      {rule.text === "colour" && (
        <input
          type="color"
          aria-label={`${rule.label} picker`}
          value={isBrandHex(raw) ? raw.toLowerCase() : ""}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1 h-8 w-12"
        />
      )}

      {raw === "" ? (
        <p>Not set: {rule.absentMeans} applies.</p>
      ) : (
        <button type="button" onClick={() => onChange("")} className="underline">
          Remove this key
        </button>
      )}

      {rule.key === COEFFICIENT && problem === null && raw !== "" && (
        <p data-testid="coefficient-meaning">
          {Number(raw) === 0
            ? "0% means Not taking new orders."
            : `${String(Number(raw) / 10)}% of normal capacity.`}
        </p>
      )}

      {problem !== null && (
        <p role="alert" data-testid={`problem-${rule.key}`}>
          {problem}
        </p>
      )}
    </div>
  );
}
